import { useState, useRef, useEffect } from "react";
import { X, Camera, CheckCircle2, ScanFace, RotateCcw } from "lucide-react";
import { useMotion } from "@/lib/safeMotion";
import { useFaceVerification } from "@/hooks/useFaceVerification";
import FaceIDScanner from "@/components/app/FaceIDScanner";
import { toast } from "sonner";

interface FaceCaptureModalProps {
  officerId: string;
  officerName: string;
  currentPhotoUrl?: string | null;
  onClose: () => void;
  onCaptured: (url: string) => void;
}

const FaceCaptureModal = ({ officerId, officerName, currentPhotoUrl, onClose, onCaptured }: FaceCaptureModalProps) => {
  const { motion } = useMotion();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [captured, setCaptured] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const { startCamera, stopCamera, captureFrame, scanningState } = useFaceVerification();

  useEffect(() => {
    if (videoRef.current) {
      startCamera(videoRef.current).then((success) => {
        if (success) {
          videoRef.current!.onloadeddata = () => setCameraReady(true);
        }
      });
    }
    return () => stopCamera();
  }, [startCamera, stopCamera]);

  const handleCapture = () => {
    const frame = captureFrame();
    if (frame) {
      setPreviewUrl(frame);
      setCaptured(true);
    }
  };

  const handleRetake = () => {
    setCaptured(false);
    setPreviewUrl(null);
  };

  const handleSave = async () => {
    setUploading(true);
    try {
      // Need to re-set the frame since we're using the hook
      // The captureFrame won't work since camera may be showing preview
      // Instead upload directly from the previewUrl
      if (!previewUrl) throw new Error("No image captured");

      // Convert base64 to blob
      const base64 = previewUrl.split(",")[1];
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "image/jpeg" });

      const { supabase } = await import("@/integrations/supabase/client");
      const filePath = `${officerId}/reference.jpg`;

      const { error: uploadError } = await supabase.storage
        .from("face-photos")
        .upload(filePath, blob, { upsert: true, contentType: "image/jpeg" });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("face-photos").getPublicUrl(filePath);
      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      await supabase.from("officers").update({ face_photo_url: publicUrl }).eq("id", officerId);

      toast.success(`Face photo saved for ${officerName}`);
      onCaptured(publicUrl);
      onClose();
    } catch (err: any) {
      toast.error(err.message || "Failed to save face photo");
    }
    setUploading(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--app-bg)/0.95)] backdrop-blur-sm font-outfit">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="app-card rounded-2xl p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-bebas text-xl text-[hsl(var(--app-text-primary))]">CAPTURE FACE PHOTO</h3>
            <p className="text-xs text-[hsl(var(--app-text-muted))]">{officerName}</p>
          </div>
          <button onClick={() => { stopCamera(); onClose(); }} className="p-1.5 rounded-lg text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] hover:bg-[hsl(var(--app-surface-2))] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {currentPhotoUrl && (
          <div className="mb-4 flex items-center gap-3 p-3 rounded-xl bg-[hsl(var(--app-green)/0.06)] border border-[hsl(var(--app-green)/0.15)]">
            <img src={currentPhotoUrl} alt="Current" className="w-10 h-10 rounded-full object-cover border-2 border-[hsl(var(--app-green)/0.3)]" />
            <div>
              <p className="text-xs font-medium text-[hsl(var(--app-green))]">Current reference photo</p>
              <p className="text-[10px] text-[hsl(var(--app-text-muted))]">Capturing a new photo will replace this one</p>
            </div>
          </div>
        )}

        <div className="mb-6 flex justify-center">
          {!captured ? (
            <FaceIDScanner 
              videoRef={videoRef} 
              state={scanningState === "idle" ? "searching" : scanningState} 
            />
          ) : previewUrl ? (
            <div className="relative w-64 h-64 md:w-72 md:h-72 rounded-full overflow-hidden border-4 border-[hsl(var(--app-green))] shadow-[0_0_30px_rgba(0,128,0,0.3)]">
              <img src={previewUrl} alt="Captured" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-[hsl(var(--app-green)/0.1)] flex items-center justify-center backdrop-blur-[1px]">
                 <CheckCircle2 className="w-16 h-16 text-[hsl(var(--app-green))]" />
              </div>
            </div>
          ) : null}
        </div>

        {captured && (
          <div className="flex items-center justify-center gap-2 mb-4 p-2 rounded-xl bg-[hsl(var(--app-green)/0.06)] border border-[hsl(var(--app-green)/0.15)]">
            <CheckCircle2 className="w-4 h-4 text-[hsl(var(--app-green))]" />
            <span className="text-xs text-[hsl(var(--app-green))] font-medium">Photo captured — review and save</span>
          </div>
        )}

        <div className="flex gap-3">
          {!captured ? (
            <button
              onClick={handleCapture}
              disabled={!cameraReady}
              className="flex-1 py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-semibold text-sm hover:brightness-110 transition-all flex items-center justify-center gap-2 disabled:opacity-40"
            >
              <Camera className="w-4 h-4" /> Capture Photo
            </button>
          ) : (
            <>
              <button
                onClick={handleRetake}
                className="flex-1 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] text-sm text-[hsl(var(--app-text-muted))] hover:bg-[hsl(var(--app-surface-2))] transition-colors flex items-center justify-center gap-1.5"
              >
                <RotateCcw className="w-4 h-4" /> Retake
              </button>
              <button
                onClick={handleSave}
                disabled={uploading}
                className="flex-1 py-3 rounded-xl bg-[hsl(var(--app-green))] text-white font-semibold text-sm hover:brightness-110 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <CheckCircle2 className="w-4 h-4" /> {uploading ? "Saving..." : "Save Photo"}
              </button>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default FaceCaptureModal;
